import { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  PencilSimple,
  Drop,
  Spinner,
  Power,
  Percent,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Product Owner directive 2026-07-03 — OPS-002.1 Product-Based Tax
// Handling. The Product record is the authoritative source for
// per-product tax applicability; global tax rates continue to live in
// Tax & Pricing Settings.
const DEFAULT_TAX_FLAGS = {
  applies_hst: true,
  applies_ontario_road_tax: true,
  applies_federal_carbon_tax: true,
};
const DEFAULT_PRICING = {
  product_family: "",
  tax_category: "",
  pricing_category: "",
  effective_date: "",
  notes: "",
};
const PRODUCT_FAMILIES = [
  "Gasoline", "Diesel", "Heating Fuel", "Propane", "Fluid", "Lubricant", "Other",
];

const EMPTY = {
  product_name: "",
  short_description: "",
  status: "Active",
  tax_flags: { ...DEFAULT_TAX_FLAGS },
  pricing: { ...DEFAULT_PRICING },
};

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/products");
      setProducts(res.data);
    } catch {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, tax_flags: { ...DEFAULT_TAX_FLAGS }, pricing: { ...DEFAULT_PRICING } });
    setOpen(true);
  };

  const openEdit = (product) => {
    setEditing(product);
    setForm({
      product_name: product.product_name,
      short_description: product.short_description,
      status: product.status,
      tax_flags: { ...DEFAULT_TAX_FLAGS, ...(product.tax_flags || {}) },
      pricing: {
        product_family: product.pricing?.product_family || "",
        tax_category: product.pricing?.tax_category || "",
        pricing_category: product.pricing?.pricing_category || "",
        effective_date: product.pricing?.effective_date || "",
        notes: product.pricing?.notes || "",
      },
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.product_name.trim() || !form.short_description.trim()) {
      toast.error("Product name and description are required");
      return;
    }
    setSaving(true);
    try {
      // Strip empty placeholders so PUT doesn't overwrite existing null
      // placeholders with empty strings and vice versa.
      const pricing = {};
      for (const k of Object.keys(DEFAULT_PRICING)) {
        const v = (form.pricing[k] ?? "").toString().trim();
        pricing[k] = v === "" ? null : v;
      }
      const payload = {
        product_name: form.product_name.trim(),
        short_description: form.short_description.trim(),
        status: form.status,
        tax_flags: form.tax_flags,
        pricing,
      };
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product added");
      }
      setOpen(false);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Save failed";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (product) => {
    const next = product.status === "Active" ? "Inactive" : "Active";
    try {
      await api.put(`/products/${product.id}`, { status: next });
      toast.success(`${product.product_name} is now ${next}`);
      await load();
    } catch {
      toast.error("Update failed");
    }
  };

  const activeCount = products.filter((p) => p.status === "Active").length;

  return (
    <div data-testid="products-page">
      <PageHeader
        title="Products"
        description="Manage the fuel products offered to customers. Each product defines which global taxes apply — the Fuel Pricing Calculator reads this directly."
        actions={
          <Button
            data-testid="add-product-button"
            onClick={openCreate}
            className="bg-[#1A1A1A] hover:bg-[#4D4D4D]"
          >
            <Plus size={16} weight="bold" className="mr-1.5" /> Add Product
          </Button>
        }
      />

      <div className="mb-5 grid grid-cols-2 gap-4 sm:grid-cols-3">
        <StatCard label="Total Products" value={products.length} testid="stat-total" />
        <StatCard label="Active" value={activeCount} tone="green" testid="stat-active" />
        <StatCard label="Inactive" value={products.length - activeCount} tone="slate" testid="stat-inactive" />
      </div>

      <Card className="border-slate-200 shadow-sm">
        <div className="border-b border-slate-200 px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          Product List
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-sm text-slate-500">
            <Spinner size={18} className="mr-2 animate-spin" /> Loading products…
          </div>
        ) : products.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-500">
            No products yet. Click <strong>Add Product</strong> to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="products-table" className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3 font-semibold">Product Name</th>
                  <th className="px-5 py-3 font-semibold">Family</th>
                  <th className="px-5 py-3 font-semibold">Applicable Taxes</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr
                    key={p.id}
                    data-testid={`product-row-${p.id}`}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60"
                  >
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-blue-50">
                          <Drop size={16} weight="fill" className="text-[#1A1A1A]" />
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">{p.product_name}</div>
                          <div className="text-xs text-slate-500">{p.short_description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-slate-700" data-testid={`product-family-${p.id}`}>
                      {p.pricing?.product_family || <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-5 py-3">
                      <TaxBadges flags={p.tax_flags} productId={p.id} />
                    </td>
                    <td className="px-5 py-3">
                      <StatusPill status={p.status} />
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex justify-end gap-1.5">
                        <Button
                          data-testid={`edit-product-${p.id}`}
                          variant="outline"
                          size="sm"
                          className="h-8 border-slate-200"
                          onClick={() => openEdit(p)}
                        >
                          <PencilSimple size={14} weight="bold" className="mr-1" /> Edit
                        </Button>
                        <Button
                          data-testid={`toggle-product-${p.id}`}
                          variant="outline"
                          size="sm"
                          className="h-8 border-slate-200"
                          onClick={() => toggleStatus(p)}
                        >
                          <Power size={14} weight="bold" className="mr-1" />
                          {p.status === "Active" ? "Deactivate" : "Activate"}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        {/*
          Administration dialog standard pattern (Product Owner 2026-07-03):
          - Dialog capped at max-h-[90vh] via base DialogContent
          - `p-0 overflow-hidden flex flex-col` removes default padding so
            header/footer can control their own padding + stickiness.
          - Header sticky-top / body flex-1 scrolls / footer sticky-bottom.
          - Save + Cancel remain visible on any viewport without page scroll.
        */}
        <DialogContent
          data-testid="product-dialog"
          className="sm:max-w-2xl p-0 overflow-hidden flex flex-col"
        >
          <DialogHeader className="sticky top-0 z-10 border-b border-slate-200 bg-white px-6 pt-6 pb-3">
            <DialogTitle className="font-display">
              {editing ? "Edit Product" : "Add Product"}
            </DialogTitle>
            <DialogDescription>
              {editing
                ? "Update product details, tax applicability, and pricing metadata. Inactive products are hidden from the New Order screen."
                : "Define a fuel product. Tax applicability here determines which global taxes the Fuel Pricing Calculator applies to this product."}
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-5">
            {/* ---- Basic details ---- */}
            <section>
              <div className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Basic Details
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Label className="mb-1.5 block text-xs font-semibold text-slate-700">Product Name *</Label>
                  <Input
                    data-testid="product-form-name"
                    value={form.product_name}
                    onChange={(e) => setForm({ ...form, product_name: e.target.value })}
                    placeholder="e.g. Clear Diesel"
                    autoFocus
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="mb-1.5 block text-xs font-semibold text-slate-700">Short Description *</Label>
                  <Textarea
                    data-testid="product-form-description"
                    value={form.short_description}
                    onChange={(e) => setForm({ ...form, short_description: e.target.value })}
                    placeholder="Brief description shown on order screens"
                    rows={2}
                  />
                </div>
                <div>
                  <Label className="mb-1.5 block text-xs font-semibold text-slate-700">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="product-form-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-1.5 block text-xs font-semibold text-slate-700">Product Family</Label>
                  <Select
                    value={form.pricing.product_family || ""}
                    onValueChange={(v) => setForm({ ...form, pricing: { ...form.pricing, product_family: v } })}
                  >
                    <SelectTrigger data-testid="product-form-family">
                      <SelectValue placeholder="Select family (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {PRODUCT_FAMILIES.map((f) => (
                        <SelectItem key={f} value={f}>{f}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </section>

            {/* ---- Pricing & Tax ---- */}
            <section className="rounded-md border border-slate-200 bg-slate-50/60 p-4" data-testid="product-form-tax-section">
              <div className="mb-2 flex items-center gap-2">
                <Percent size={16} weight="bold" className="text-[#1A1A1A]" />
                <div className="text-sm font-semibold text-slate-900">Pricing &amp; Tax</div>
              </div>
              <p className="mb-3 text-xs text-slate-600">
                Which globally-configured taxes apply to this product. Tax rates
                are managed under <strong>Administration → Tax &amp; Pricing</strong>.
              </p>
              <div className="space-y-2">
                <TaxCheckbox
                  testid="product-tax-hst"
                  label="Applies HST"
                  hint="Harmonized Sales Tax — normally applies to almost every fuel product."
                  checked={form.tax_flags.applies_hst}
                  onChange={(v) => setForm({ ...form, tax_flags: { ...form.tax_flags, applies_hst: v } })}
                />
                <TaxCheckbox
                  testid="product-tax-road"
                  label="Applies Ontario Road Tax"
                  hint="On-road diesel & gasoline only. Dyed / off-road / heating fuels are exempt."
                  checked={form.tax_flags.applies_ontario_road_tax}
                  onChange={(v) => setForm({ ...form, tax_flags: { ...form.tax_flags, applies_ontario_road_tax: v } })}
                />
                <TaxCheckbox
                  testid="product-tax-carbon"
                  label="Applies Federal Carbon Tax"
                  hint="Combustion fuels only. Non-combustion products (DEF, lubricants) are exempt."
                  checked={form.tax_flags.applies_federal_carbon_tax}
                  onChange={(v) => setForm({ ...form, tax_flags: { ...form.tax_flags, applies_federal_carbon_tax: v } })}
                />
              </div>
            </section>

            {/* ---- Optional metadata ---- */}
            <section>
              <div className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Additional Metadata (Optional)
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label className="mb-1.5 block text-xs font-semibold text-slate-700">Tax Category</Label>
                  <Input
                    data-testid="product-form-tax-category"
                    value={form.pricing.tax_category || ""}
                    onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, tax_category: e.target.value } })}
                    placeholder="Reserved for future tax profiles"
                  />
                </div>
                <div>
                  <Label className="mb-1.5 block text-xs font-semibold text-slate-700">Pricing Category</Label>
                  <Input
                    data-testid="product-form-pricing-category"
                    value={form.pricing.pricing_category || ""}
                    onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, pricing_category: e.target.value } })}
                    placeholder="e.g. Premium, Retail, Wholesale"
                  />
                </div>
                <div>
                  <Label className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Effective Date <span className="font-normal text-slate-400">(reserved)</span>
                  </Label>
                  <Input
                    data-testid="product-form-effective-date"
                    type="date"
                    value={form.pricing.effective_date || ""}
                    onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, effective_date: e.target.value } })}
                  />
                  <p className="mt-1 text-[11px] text-slate-500">
                    Historical tax calculations — feature coming in V1.2.
                  </p>
                </div>
                <div>
                  <Label className="mb-1.5 block text-xs font-semibold text-slate-700">Notes</Label>
                  <Input
                    data-testid="product-form-notes"
                    value={form.pricing.notes || ""}
                    onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, notes: e.target.value } })}
                    placeholder="Internal notes about this product"
                  />
                </div>
              </div>
            </section>
          </DialogBody>

          <DialogFooter className="sticky bottom-0 z-10 border-t border-slate-200 bg-white px-6 py-4">
            <Button
              data-testid="product-form-cancel"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#1A1A1A] hover:bg-[#4D4D4D]"
              data-testid="product-form-save"
            >
              {saving ? (
                <>
                  <Spinner size={14} className="mr-1.5 animate-spin" /> Saving…
                </>
              ) : editing ? (
                "Save Changes"
              ) : (
                "Add Product"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TaxCheckbox({ label, hint, checked, onChange, testid }) {
  return (
    <label
      className="flex cursor-pointer items-start gap-2.5 rounded border border-slate-200 bg-white p-2.5 hover:border-slate-300"
      data-testid={`${testid}-row`}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={onChange}
        data-testid={testid}
        className="mt-0.5"
      />
      <div className="flex-1">
        <div className="text-sm font-medium text-slate-900">{label}</div>
        <div className="text-xs text-slate-500">{hint}</div>
      </div>
    </label>
  );
}

function TaxBadges({ flags, productId }) {
  if (!flags) return <span className="text-xs text-slate-400">—</span>;
  const items = [
    { k: "HST", on: flags.applies_hst, testid: "hst" },
    { k: "Road", on: flags.applies_ontario_road_tax, testid: "road" },
    { k: "Carbon", on: flags.applies_federal_carbon_tax, testid: "carbon" },
  ];
  return (
    <div className="flex flex-wrap gap-1" data-testid={`product-taxes-${productId}`}>
      {items.map((it) => (
        <span
          key={it.k}
          data-testid={`product-tax-badge-${it.testid}-${productId}`}
          data-active={it.on}
          className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] font-medium ${
            it.on
              ? "border-green-200 bg-green-50 text-green-700"
              : "border-slate-200 bg-slate-50 text-slate-400 line-through"
          }`}
        >
          {it.k}
        </span>
      ))}
    </div>
  );
}

function StatCard({ label, value, tone = "blue", testid }) {
  const toneStyle =
    tone === "green"
      ? "text-green-700"
      : tone === "slate"
        ? "text-slate-600"
        : "text-[#1A1A1A]";
  return (
    <Card data-testid={testid} className="border-slate-200 p-4 shadow-sm">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1 font-display text-2xl font-bold ${toneStyle}`}>{value}</div>
    </Card>
  );
}

function StatusPill({ status }) {
  const styles =
    status === "Active"
      ? "bg-green-50 text-green-700 border-green-200"
      : "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${styles}`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${status === "Active" ? "bg-green-500" : "bg-slate-400"}`}
      />
      {status}
    </span>
  );
}
